import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Check, X, Shield, Settings2, Trash2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ROLE_LABELS = { superadmin: "Super Admin", admin: "Admin", segreteria: "Segreteria", manutentore: "Manutentore" };
const STATUS_BADGE = {
  approved: "bg-[#39FF14]/15 text-[#39FF14]",
  pending: "bg-[#FF9500]/15 text-[#FF9500]",
  rejected: "bg-[#FF3B30]/15 text-[#FF3B30]",
};
const STATUS_LABEL = { approved: "Approvato", pending: "In attesa", rejected: "Rifiutato" };

export default function Admin() {
  const { user, hasPerm } = useAuth();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], roles: [], role_defaults: {} });
  const [permUser, setPermUser] = useState(null);
  const [permSel, setPermSel] = useState([]);

  const load = async () => {
    const u = await api.get("/users");
    setUsers(u.data);
    const m = await api.get("/permissions");
    setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  const update = async (u, patch) => {
    try { await api.put(`/users/${u.user_id}`, patch); await load(); toast.success("Aggiornato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (u) => {
    try { await api.delete(`/users/${u.user_id}`); toast.success("Utente eliminato"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const openPerms = (u) => {
    setPermUser(u);
    setPermSel(u.raw_permissions?.length ? u.raw_permissions : meta.role_defaults[u.role] || []);
  };
  const togglePerm = (key) => setPermSel((s) => s.includes(key) ? s.filter((x) => x !== key) : [...s, key]);
  const savePerms = async () => {
    await update(permUser, { permissions: permSel });
    setPermUser(null);
  };
  const resetToRole = () => { if (permUser) setPermSel(meta.role_defaults[permUser.role] || []); };

  const groups = [...new Set(meta.permissions.map((p) => p.group))];
  const pending = users.filter((u) => u.status === "pending");

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Gestione utenti e ruoli</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">AMMINISTRAZIONE</h1>
      </div>

      {pending.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><span className="h-1.5 w-1.5 bg-[#FF9500]" /> In attesa di approvazione ({pending.length})</h2>
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.user_id} data-testid={`pending-user-${u.user_id}`} className="bg-[#141414] border border-[#FF9500]/40 rounded-sm p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9"><AvatarImage src={u.picture} /><AvatarFallback className="bg-zinc-800">{u.name?.[0]}</AvatarFallback></Avatar>
                  <div><div className="font-medium">{u.name}</div><div className="text-xs text-zinc-500">{u.email}</div></div>
                </div>
                <div className="flex items-center gap-2">
                  <Select defaultValue={u.role} onValueChange={(v) => update(u, { role: v })}>
                    <SelectTrigger className="w-40 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid={`pending-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800">
                      {meta.roles.filter((r) => r !== "superadmin" || user.role === "superadmin").map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button onClick={() => update(u, { status: "approved" })} data-testid={`approve-${u.user_id}`} className="flex items-center gap-1 bg-[#39FF14] text-[#0A0A0A] font-bold px-3 py-2 rounded-sm text-sm hover:bg-[#2dd60f]"><Check className="h-4 w-4" /> Approva</button>
                  <button onClick={() => update(u, { status: "rejected" })} data-testid={`reject-${u.user_id}`} className="flex items-center gap-1 border border-zinc-700 px-3 py-2 rounded-sm text-sm hover:bg-zinc-800"><X className="h-4 w-4" /> Rifiuta</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Users className="h-5 w-5 text-[#f98125]" /> Tutti gli utenti</h2>
      <div className="bg-[#141414] border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-bold text-zinc-500 uppercase tracking-wider border-b border-zinc-800">
                <th className="text-left px-4 py-3">Utente</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Stato</th>
                <th className="text-left px-4 py-3">Ruolo</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-zinc-800/50 hover:bg-[#1a1a1a] transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8"><AvatarImage src={u.picture} /><AvatarFallback className="bg-zinc-800 text-xs">{u.name?.[0]}</AvatarFallback></Avatar>
                      <div><div className="font-medium flex items-center gap-1.5">{u.name} {u.role === "superadmin" && <Shield className="h-3.5 w-3.5 text-[#f98125]" />}</div><div className="text-xs text-zinc-500">{u.email}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell"><span className={`text-xs px-2 py-1 rounded-sm ${STATUS_BADGE[u.status]}`}>{STATUS_LABEL[u.status]}</span></td>
                  <td className="px-4 py-3">
                    {u.role === "superadmin" && user.role !== "superadmin" ? (
                      <span className="text-sm text-[#f98125]">{ROLE_LABELS[u.role]}</span>
                    ) : (
                      <Select defaultValue={u.role} onValueChange={(v) => update(u, { role: v })}>
                        <SelectTrigger className="w-40 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-zinc-800">
                          {meta.roles.filter((r) => r !== "superadmin" || user.role === "superadmin").map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {hasPerm("roles.manage") && (
                        <button onClick={() => openPerms(u)} data-testid={`perms-btn-${u.user_id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm" title="Permessi granulari"><Settings2 className="h-4 w-4" /></button>
                      )}
                      {u.role !== "superadmin" && u.user_id !== user.user_id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`delete-user-${u.user_id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#141414] border-zinc-800">
                            <AlertDialogHeader><AlertDialogTitle>Eliminare {u.name}?</AlertDialogTitle><AlertDialogDescription>L'utente perderà l'accesso. Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(u)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!permUser} onOpenChange={(o) => !o && setPermUser(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Settings2 className="h-5 w-5 text-[#f98125]" /> Permessi · {permUser?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">Permessi granulari per singola attività. Sovrascrivono i default del ruolo {ROLE_LABELS[permUser?.role]}.</p>
          <div className="space-y-5 py-2">
            {groups.map((g) => (
              <div key={g}>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{g}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {meta.permissions.filter((p) => p.group === g).map((p) => (
                    <label key={p.key} data-testid={`perm-${p.key}`} className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer transition-colors ${permSel.includes(p.key) ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800 hover:border-zinc-700"}`}>
                      <Checkbox checked={permSel.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={resetToRole} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors mr-auto">Reset a ruolo</button>
            <button onClick={() => setPermUser(null)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={savePerms} data-testid="save-perms-btn" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors">Salva permessi</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
